import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, MapPin, Phone, Mail, ExternalLink, Printer, ChevronDown, ChevronUp, Globe, Search, X, LogIn, UserPlus, Ticket } from 'lucide-react';
import { VoucherService } from '@/services/voucherService';
import { PartnerService } from '@/services/partnerService';
import { Voucher } from '@/services/voucherService';
import { Partner } from '@/services/partnerService';
import Navbar from '@/components/Navbar';
import { getPartnerLogoUrl } from '@/config/api';
import Footer from '@/components/Footer';
import { useAuthState } from '@/hooks/useAuthState';
import { useNavigate } from 'react-router-dom';
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";
import heroPartners from "@/assets/hero-partners.jpg";

const VouchersPage = () => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const partnerFilter = searchParams.get('partner');
  const [vouchers, setVouchers] = useState<(Voucher & { partner: Partner })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allVouchers, setAllVouchers] = useState<(Voucher & { partner: Partner })[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "random">("random");

  // Array de imagens de Muro Alto para o banner rotativo
  const heroImages = [heroMuroAlto1, heroMuroAlto2, heroMuroAlto3, heroMuroAlto4, heroMuroAlto5];
  
  // Seleciona uma imagem aleatória a cada render (refresh da página)
  const randomHeroImage = useMemo(() => {
    return heroImages[Math.floor(Math.random() * heroImages.length)];
  }, []);

  // Inicializar campo de busca com o filtro da URL
  useEffect(() => {
    if (partnerFilter) {
      setSearchTerm(partnerFilter);
    }
  }, [partnerFilter]);

  useEffect(() => {
    loadVouchers();
  }, [partnerFilter]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, hasMore, loadingMore]);

  const loadVouchers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar todos os parceiros ativos
      const partners = await PartnerService.getAllPartners();
      
      // Buscar vouchers de todos os parceiros
      const allVouchersData = [];
      for (const partner of partners) {
        try {
          const partnerVouchers = await VoucherService.getVouchersByPartner(partner.id);
          // Adicionar informações do parceiro a cada voucher
          const vouchersWithPartner = partnerVouchers.map(voucher => ({
            ...voucher,
            partner
          }));
          allVouchersData.push(...vouchersWithPartner);
        } catch (err) {
          console.error(`Erro ao buscar vouchers do parceiro ${partner.name}:`, err);
        }
      }

      // Filtrar apenas vouchers ativos e não deletados
      let activeVouchers = allVouchersData.filter(voucher => 
        voucher.is_active && !voucher.deleted_at
      );

      // Aplicar filtro por nome do parceiro se fornecido
      if (partnerFilter) {
        activeVouchers = activeVouchers.filter(voucher =>
          voucher.partner.name.toLowerCase().includes(partnerFilter.toLowerCase())
        );
      }

      setAllVouchers(activeVouchers);
    } catch (err) {
      console.error('Erro ao carregar vouchers:', err);
      setError('Erro ao carregar vouchers. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Obter tipos de serviço únicos dos parceiros
  const serviceTypes = useMemo(() => {
    const types = new Set<string>();
    allVouchers.forEach(voucher => {
      if (voucher.partner.service_type && voucher.partner.service_type.trim() !== '') {
        types.add(voucher.partner.service_type);
      }
    });
    return Array.from(types).sort();
  }, [allVouchers]);

  // Filtrar e ordenar vouchers
  const filteredVouchers = useMemo(() => {
    let filtered = [...allVouchers];

    // Filtro por termo de busca
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(voucher =>
        voucher.title.toLowerCase().includes(term) ||
        voucher.description?.toLowerCase().includes(term) ||
        voucher.partner.name.toLowerCase().includes(term) ||
        voucher.code.toLowerCase().includes(term)
      );
    }

    // Filtro por tipo de serviço
    if (serviceTypeFilter) {
      filtered = filtered.filter(voucher => voucher.partner.service_type === serviceTypeFilter);
    }

    // Ordenação alfabética pelo nome da empresa
    filtered.sort((a, b) => {
      const nameA = a.partner.name.toLowerCase();
      const nameB = b.partner.name.toLowerCase();
      if (sortOrder === "asc") {
        return nameA.localeCompare(nameB, 'pt-BR');
      } else {
        return nameB.localeCompare(nameA, 'pt-BR');
      }
    });

    return filtered;
  }, [allVouchers, searchTerm, serviceTypeFilter, sortOrder]);

  // Agrupar vouchers por empresa (para manter lado a lado) e depois achatar
  const sortedVouchers = useMemo(() => {
    const groups = new Map<number, (Voucher & { partner: Partner })[]>();

    filteredVouchers.forEach(voucher => {
      const partnerId = voucher.partner.id;
      if (!groups.has(partnerId)) {
        groups.set(partnerId, []);
      }
      groups.get(partnerId)!.push(voucher);
    });

    // Converter Map para Array de grupos
    const groupsArray = Array.from(groups.entries()).map(([partnerId, vouchers]) => ({
      partnerId,
      vouchers
    }));

    // Ordenar grupos por nome da empresa ou aleatoriamente
    let sortedGroups;
    if (sortOrder === "random") {
      // Algoritmo Fisher-Yates para embaralhar
      const shuffled = [...groupsArray];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      sortedGroups = shuffled;
    } else {
      sortedGroups = groupsArray.sort((a, b) => {
        const nameA = a.vouchers[0].partner.name.toLowerCase();
        const nameB = b.vouchers[0].partner.name.toLowerCase();
        if (sortOrder === "asc") {
          return nameA.localeCompare(nameB, 'pt-BR');
        } else {
          return nameB.localeCompare(nameA, 'pt-BR');
        }
      });
    }

    // Achatar os grupos em uma única lista (vouchers do mesmo parceiro ficam juntos)
    return sortedGroups.flatMap(group => group.vouchers);
  }, [filteredVouchers, sortOrder]);

  const loadPage = (page: number, vouchersData: (Voucher & { partner: Partner })[]) => {
    const itemsPerPage = 12; // Número de vouchers por página
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageVouchers = vouchersData.slice(startIndex, endIndex);
    
    if (page === 1) {
      setVouchers(pageVouchers);
    } else {
      setVouchers(prev => [...prev, ...pageVouchers]);
    }
    
    setCurrentPage(page);
    setHasMore(endIndex < vouchersData.length);
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      setTimeout(() => {
        loadPage(currentPage + 1, sortedVouchers);
        setLoadingMore(false);
      }, 500);
    }
  };

  // Recarregar página quando vouchers ordenados mudarem
  useEffect(() => {
    if (sortedVouchers.length > 0) {
      loadPage(1, sortedVouchers);
    } else if (allVouchers.length > 0 && sortedVouchers.length === 0) {
      // Se não há vouchers filtrados, limpar a lista
      setVouchers([]);
      setCurrentPage(1);
      setHasMore(false);
    }
  }, [sortedVouchers.length, searchTerm, serviceTypeFilter, sortOrder]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'Não informado';
    }
    // Extrair apenas a parte da data (YYYY-MM-DD)
    const dateOnly = dateString.split(' ')[0].split('T')[0];
    const [year, month, day] = dateOnly.split('-');
    // Criar Date usando componentes locais para evitar timezone
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isVoucherValid = (voucher: Voucher) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Se não tem data de início, considerar válido desde sempre
    let startDate: Date | null = null;
    if (voucher.start_date) {
      const [year, month, day] = voucher.start_date.split(' ')[0].split('T')[0].split('-');
      startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      startDate.setHours(0, 0, 0, 0);
    }
    
    // Se não tem data de fim, considerar válido indefinidamente
    let endDate: Date | null = null;
    if (voucher.end_date) {
      const [year, month, day] = voucher.end_date.split(' ')[0].split('T')[0].split('-');
      endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      endDate.setHours(23, 59, 59, 999);
    }
    
    // Validar: se tem data início, hoje deve ser >= início
    if (startDate && today < startDate) {
      return false;
    }
    
    // Validar: se tem data fim, hoje deve ser <= fim
    if (endDate && today > endDate) {
      return false;
    }
    
    return voucher.is_active && (voucher.usage_limit === null || voucher.usage_count < voucher.usage_limit);
  };

  const toggleCardExpansion = (voucherId: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(voucherId)) {
      newExpanded.delete(voucherId);
    } else {
      newExpanded.add(voucherId);
    }
    setExpandedCards(newExpanded);
  };

  const getVoucherStatus = (voucher: Voucher) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Se não tem data de início, considerar válido desde sempre
    let startDate: Date | null = null;
    if (voucher.start_date) {
      const [year, month, day] = voucher.start_date.split(' ')[0].split('T')[0].split('-');
      startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      startDate.setHours(0, 0, 0, 0);
    }
    
    // Se não tem data de fim, considerar válido indefinidamente
    let endDate: Date | null = null;
    if (voucher.end_date) {
      const [year, month, day] = voucher.end_date.split(' ')[0].split('T')[0].split('-');
      endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      endDate.setHours(23, 59, 59, 999);
    }
    
    if (startDate && today < startDate) {
      return { text: 'Em breve', variant: 'secondary' as const };
    } else if (endDate && today > endDate) {
      return { text: 'Expirado', variant: 'destructive' as const };
    } else {
      return { text: 'Válido', variant: 'default' as const };
    }
  };

  const handlePrintVoucher = async (voucher: Voucher & { partner: Partner }) => {
    // Registrar impressão no banco de dados
    console.log('🖨️ Iniciando impressão do voucher:', voucher.id, voucher.title);
    try {
      await VoucherService.registerPrint(voucher.id);
      console.log('✅ Registro de impressão concluído');
    } catch (error) {
      console.error('❌ Erro ao registrar impressão:', error);
      // Continuar com a impressão mesmo se o registro falhar
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Voucher - ${voucher.title}</title>
        <style>
          body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
          .voucher { border: 3px solid #000; padding: 20px; margin: 10px 0; page-break-inside: avoid; }
          .header { text-align: center; margin-bottom: 20px; }
          .logo { max-width: 200px; height: auto; margin-bottom: 15px; }
          .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .partner-info { background: #f8f9fa; padding: 15px; margin: 15px 0; border-radius: 8px; border-left: 4px solid #2563eb; }
          .partner-header { display: flex; align-items: center; margin-bottom: 10px; }
          .partner-logo { max-width: 60px; max-height: 60px; margin-right: 15px; border-radius: 8px; }
          .partner-name { font-size: 18px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
          .partner-details { font-size: 14px; color: #666; line-height: 1.4; }
          .code { font-size: 32px; font-weight: bold; letter-spacing: 3px; background: #f0f0f0; padding: 15px; text-align: center; margin: 20px 0; border: 2px dashed #000; }
          .discount { font-size: 20px; font-weight: bold; color: #2563eb; margin: 15px 0; }
          .details { margin: 15px 0; }
          .description { margin: 15px 0; font-style: italic; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
          .validity { font-weight: bold; color: #059669; }
          .instructions { background: #f8f9fa; padding: 15px; border-left: 4px solid #2563eb; margin: 20px 0; }
          .live-stay-brand { text-align: center; margin-top: 15px; font-size: 14px; color: #2563eb; font-weight: bold; }
          
          @media print {
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .voucher { border: 3px solid #000; padding: 20px; margin: 10px 0; page-break-inside: avoid; }
            .header { text-align: center; margin-bottom: 20px; }
            .logo { max-width: 200px; height: auto; margin-bottom: 15px; }
            .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .partner-info { background: #f8f9fa; padding: 15px; margin: 15px 0; border-radius: 8px; border-left: 4px solid #2563eb; }
            .partner-header { display: flex; align-items: center; margin-bottom: 10px; }
            .partner-logo { max-width: 60px; max-height: 60px; margin-right: 15px; border-radius: 8px; }
            .partner-name { font-size: 18px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
            .partner-details { font-size: 14px; color: #666; line-height: 1.4; }
            .code { font-size: 32px; font-weight: bold; letter-spacing: 3px; background: #f0f0f0; padding: 15px; text-align: center; margin: 20px 0; border: 2px dashed #000; }
            .discount { font-size: 20px; font-weight: bold; color: #2563eb; margin: 15px 0; }
            .details { margin: 15px 0; }
            .description { margin: 15px 0; font-style: italic; }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
            .validity { font-weight: bold; color: #059669; }
            .instructions { background: #f8f9fa; padding: 15px; border-left: 4px solid #2563eb; margin: 20px 0; }
            .live-stay-brand { text-align: center; margin-top: 15px; font-size: 14px; color: #2563eb; font-weight: bold; }
          }
        </style>
      </head>
      <body>
        <div class="voucher">
          <div class="header">
            <img src="http://localhost:3000/live-stay-logo.png" alt="LiveStay" class="logo">
            <div class="title">${voucher.title}</div>
            <div class="discount">
              ${voucher.discount_type === 'percentage' ? 'Desconto de ' + voucher.discount_value + '%' : 'Desconto de R$ ' + voucher.discount_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </div>
          
          <div class="partner-info">
            <div class="partner-header">
              ${voucher.partner.logo ? `<img src="${getPartnerLogoUrl(voucher.partner.logo)}" alt="${voucher.partner.name}" class="partner-logo" onerror="this.style.display='none'">` : ''}
              <div class="partner-name">${voucher.partner.name}</div>
            </div>
            <div class="partner-details">
              ${voucher.partner.address ? `<div><strong>Endereço:</strong> ${voucher.partner.address}</div>` : ''}
              ${voucher.partner.phone ? `<div><strong>Telefone:</strong> ${voucher.partner.phone}</div>` : ''}
              ${voucher.partner.email ? `<div><strong>E-mail:</strong> ${voucher.partner.email}</div>` : ''}
            </div>
          </div>
          
          <div class="code">${voucher.code}</div>
          
          <div class="details">
            <div class="validity">
              Válido de ${formatDate(voucher.start_date)} até ${formatDate(voucher.end_date)}
            </div>
          </div>
          
          ${voucher.description ? `<div class="description">${voucher.description}</div>` : ''}
          
          <div class="instructions">
            <strong>Como usar:</strong><br>
            Apresente este voucher impresso ou digital no estabelecimento para receber seu desconto.
          </div>
          
          <div class="live-stay-brand">
            Powered by LiveStay
          </div>
          
          <div class="footer">
            Voucher gerado em ${formatDateTime(voucher.created_at)}
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Aguarda o carregamento da página antes de tentar imprimir
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
    
    // Fecha a janela após a impressão (ou cancelamento)
    printWindow.onafterprint = () => {
      printWindow.close();
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar 
          isAuthenticated={isAuthenticated} 
          isOwner={userType === "owner"} 
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          userType={userType}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando vouchers...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar 
          isAuthenticated={isAuthenticated} 
          isOwner={userType === "owner"} 
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          userType={userType}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="text-destructive text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold mb-2">Erro ao carregar vouchers</h1>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadVouchers}>Tentar novamente</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-end pb-8">
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
          style={{ backgroundImage: `url(${randomHeroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-3 drop-shadow-lg">
            Vouchers de Desconto
          </h1>
          <p className="text-xl md:text-2xl text-white/90 drop-shadow-lg max-w-3xl mx-auto">
            Aproveite os descontos exclusivos dos nossos parceiros em Porto de Galinhas
          </p>
        </div>
      </section>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Filtros */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Barra de busca */}
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar vouchers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pl-10 pr-10 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  {partnerFilter && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('partner');
                        setSearchParams(newSearchParams);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                      title="Limpar filtro"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filtro por tipo de serviço */}
              <div className="w-full md:w-64">
                <Select value={serviceTypeFilter || "all"} onValueChange={(value) => setServiceTypeFilter(value === "all" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {serviceTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ordenação */}
              <div className="w-full md:w-48">
                <Select value={sortOrder} onValueChange={(value: "asc" | "desc" | "random") => setSortOrder(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Aleatório</SelectItem>
                    <SelectItem value="asc">A-Z</SelectItem>
                    <SelectItem value="desc">Z-A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Botão Limpar */}
              {(searchTerm || serviceTypeFilter || partnerFilter) && (
                <div className="w-full md:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("");
                      setServiceTypeFilter("");
                      const newSearchParams = new URLSearchParams(searchParams);
                      newSearchParams.delete('partner');
                      setSearchParams(newSearchParams);
                    }}
                    className="w-full md:w-auto"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Limpar
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Banner de alerta para usuários não logados */}
          {!isAuthenticated && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-lg border-2 border-orange-500/30">
              <div className="relative bg-gradient-to-r from-orange-50 via-orange-100 to-amber-50 p-6 md:p-8">
                {/* Imagem de fundo */}
                <div className="absolute inset-0 opacity-10">
                  <img 
                    src={heroPartners} 
                    alt="Parceiros" 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="relative flex flex-col md:flex-row items-center gap-6">
                  {/* Ícone e imagem destacada */}
                  <div className="flex-shrink-0 flex items-center gap-4">
                    <div className="bg-orange-500 rounded-full p-4 shadow-lg">
                      <Ticket className="h-8 w-8 text-white" />
                    </div>
                    <div className="hidden md:block w-24 h-24 rounded-lg overflow-hidden shadow-md border-2 border-orange-300">
                      <img 
                        src={heroPartners} 
                        alt="Vouchers" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  {/* Texto e botões */}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-xl md:text-2xl font-bold text-orange-700 mb-2">
                      Desbloqueie todos os benefícios dos vouchers!
                    </h3>
                    <p className="text-orange-600 mb-4 text-sm md:text-base">
                      Faça seu cadastro ou entre na sua conta para visualizar códigos de desconto exclusivos, valores promocionais e imprimir seus vouchers.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                      <Button
                        onClick={() => navigate('/login#cadastro')}
                        className="bg-orange-500 hover:bg-orange-600 text-white shadow-md"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Criar Conta
                      </Button>
                      <Button
                        onClick={() => navigate('/login#login')}
                        variant="outline"
                        className="border-orange-500 text-orange-600 hover:bg-orange-50"
                      >
                        <LogIn className="h-4 w-4 mr-2" />
                        Fazer Login
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {sortedVouchers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-muted-foreground text-6xl mb-4">🎫</div>
              <h2 className="text-2xl font-bold mb-2">Nenhum voucher disponível</h2>
              <p className="text-muted-foreground">
                Não há vouchers ativos no momento. Volte em breve para conferir as novidades!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {vouchers.map((voucher) => {
                const status = getVoucherStatus(voucher);
                const isValid = isVoucherValid(voucher);
                const isExpanded = expandedCards.has(voucher.id);
                
                return (
                  <Card key={voucher.id} className="overflow-hidden hover:shadow-lg transition-shadow h-fit">
                    <CardHeader className="pb-3">
                      {/* Nome da empresa em cima */}
                      <div className="flex items-center gap-3 mb-3">
                        {voucher.partner.logo && (
                          <img
                            src={getPartnerLogoUrl(voucher.partner.logo)}
                            alt={voucher.partner.name}
                            className="w-12 h-12 object-contain rounded-lg shadow-sm border border-gray-200 flex-shrink-0"
                            onError={(e) => e.currentTarget.style.display = 'none'}
                          />
                        )}
                        <CardTitle className="text-lg font-semibold flex-1">
                          {voucher.partner.name}
                        </CardTitle>
                      </div>
                      
                      {/* Valor do desconto embaixo com destaque - apenas se logado */}
                      {isAuthenticated && (
                        <div className="bg-primary/10 rounded-lg p-3 mb-2">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-primary mb-1">
                              {voucher.discount_type === 'percentage' ? `${voucher.discount_value}%` : `R$ ${voucher.discount_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                            </div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">desconto</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Badge de status */}
                      <div>
                        <Badge variant={status.variant}>
                          {status.text}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Código do voucher - apenas se logado */}
                      {isAuthenticated && (
                        <div className="bg-muted/50 p-3 rounded-lg">
                          <div className="font-mono text-lg font-bold text-center text-primary">
                            {voucher.code}
                          </div>
                          <div className="text-xs text-center text-muted-foreground mt-1">
                            Código do voucher
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Válido de:</span>
                          <span className="font-medium">{formatDate(voucher.start_date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Até:</span>
                          <span className="font-medium">{formatDate(voucher.end_date)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground line-clamp-2">{voucher.partner.address}</span>
                        </div>
                        {voucher.partner.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground">{voucher.partner.phone}</span>
                          </div>
                        )}
                        {voucher.partner.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground line-clamp-1">{voucher.partner.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Conteúdo expandível */}
                      {isExpanded && (
                        <div className="space-y-4 pt-4 border-t">
                          {voucher.description && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <div className="text-sm font-medium text-amber-800 mb-1">
                                📋 Regras de Uso:
                              </div>
                              <div className="text-sm text-amber-700">
                                {voucher.description}
                              </div>
                            </div>
                          )}

                          <div className="space-y-2">
                            <div className="text-sm font-medium text-foreground">Informações do Parceiro:</div>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              {voucher.partner.website && (
                                <div className="flex items-center gap-2">
                                  <Globe className="w-4 h-4" />
                                  <a 
                                    href={voucher.partner.website} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:text-primary transition-colors"
                                  >
                                    {voucher.partner.website}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        {/* Botão Imprimir - apenas se logado */}
                        {isAuthenticated && (
                          <Button 
                            onClick={() => handlePrintVoucher(voucher)}
                            className="flex-1"
                            disabled={!isValid}
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            Imprimir
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => toggleCardExpansion(voucher.id)}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                        {voucher.partner.website && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(voucher.partner.website, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Indicador de carregamento e botão "Carregar mais" */}
          {sortedVouchers.length > 0 && (
            <div className="mt-12 text-center">
              {loadingMore ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span>Carregando mais vouchers...</span>
                </div>
              ) : hasMore ? (
                <Button 
                  onClick={loadMore}
                  variant="outline"
                  className="px-8 py-2"
                >
                  Carregar Mais Vouchers
                </Button>
              ) : (
                <p className="text-muted-foreground">
                  Todos os vouchers foram carregados
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VouchersPage;
